<template>
  <aside class="filters form-group trip-filters">
    <div class="trip-filters__grid">

      <!-- Colonne gauche : Prix -->
      <div class="group-columns">
        <label>Prix</label>
        <div class="price-filter">
          <div class="price-filter__values">
            <span>{{ localeStore.formatPrice(localMinPrice) }}</span>
            <span>{{ localeStore.formatPrice(localMaxPrice) }}</span>
          </div>
          <div class="price-filter__slider-wrap">
            <div class="price-filter__track"></div>
            <div class="price-filter__range" :style="priceProgressStyle"></div>
            <input v-model.number="localMinPrice" type="range" :min="priceBounds.min" :max="priceBounds.max" :step="1"
              class="price-filter__input price-filter__input--min" />
            <input v-model.number="localMaxPrice" type="range" :min="priceBounds.min" :max="priceBounds.max" :step="1"
              class="price-filter__input price-filter__input--max" />
          </div>
        </div>
      </div>

      <!-- Colonne droite : Compagnies + Options + Footer -->
      <div class="trip-filters__right">

        <div class="trip-filters__dropdowns">
          <!-- Compagnies -->
          <div class="group-columns">
            <label>Compagnies</label>
            <div class="compact-select" v-click-outside="() => companiesOpen = false">
              <button type="button" class="dropdown-trigger" @click="companiesOpen = !companiesOpen">
                <span v-if="!selectedCompanies.length">Toutes les compagnies</span>
                <span v-else class="compact-select__trigger-text">{{ selectedCompaniesLabels.join(', ') }}</span>
              </button>
              <div v-if="companiesOpen" class="compact-select__menu">
                <button v-for="company in normalizedCompanies" :key="company.value" type="button"
                  class="compact-select__option" :class="{ 'is-selected': selectedCompanies.includes(company.value) }"
                  @click="toggleCompany(company.value)">
                  <span>{{ company.label }}</span>
                  <span v-if="selectedCompanies.includes(company.value)" class="compact-select__check">✓</span>
                </button>
                <p v-if="!normalizedCompanies.length" class="compact-select__empty">Aucune compagnie</p>
              </div>
            </div>
          </div>

          <!-- Options -->
          <div class="group-columns">
            <label>Options</label>
            <div class="compact-select" v-click-outside="() => optionsOpen = false">
              <button type="button" class="dropdown-trigger" @click="optionsOpen = !optionsOpen">
                <span v-if="!selectedOptions.length">Toutes les options</span>
                <span v-else class="compact-select__trigger-text">{{ selectedOptionsLabels.join(', ') }}</span>
              </button>
              <div v-if="optionsOpen" class="compact-select__menu">
                <button v-for="option in availableOptions" :key="option.value" type="button"
                  class="compact-select__option" :class="{ 'is-selected': selectedOptions.includes(option.value) }"
                  @click="toggleOption(option.value)">
                  <span>{{ option.label }}</span>
                  <span v-if="selectedOptions.includes(option.value)" class="compact-select__check">✓</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer collé en bas de la colonne droite -->
        <div class="btn-group trip-filters__footer">
          <span v-if="props.trips.length" class="badge badge-info">
            {{ filteredTrips.length }} trajet(s) trouvé(s) sur {{ props.trips.length }}
          </span>
          <button type="button" class="btn btn-secondary" @click="resetFilters">Réinitialiser</button>
        </div>

      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { VEHICLE_OPTIONS } from '@/enums/optionsVehicle'
import { useLocaleStore } from '@/stores/localeStore'

const localeStore = useLocaleStore()

const props = defineProps({ trips: { type: Array, default: () => [] } })
const emit = defineEmits(['update:filtered'])

const companiesOpen = ref(false)
const optionsOpen = ref(false)
const selectedCompanies = ref([])
const selectedOptions = ref([])

const vClickOutside = {
  mounted(el, binding) {
    el._handler = e => { if (!el.contains(e.target)) binding.value(e) }
    document.addEventListener('click', el._handler)
  },
  unmounted(el) {
    document.removeEventListener('click', el._handler)
  }
}

const normalizedCompanies = computed(() => {
  const map = new Map()

  for (const trip of props.trips) {
    const c = trip.company ?? trip.transportCompany ?? trip.operator ?? null
    if (!c) continue

    const value = typeof c === 'object' ? (c.id ?? c.slug ?? c.name) : c
    const label = typeof c === 'object' ? (c.name ?? c.slug ?? `Compagnie ${value}`) : String(c)

    if (value != null) map.set(value, { value, label })
  }

  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label))
})

const selectedCompaniesLabels = computed(() =>
  normalizedCompanies.value
    .filter(c => selectedCompanies.value.includes(c.value))
    .map(c => c.label)
)

function hasVehicleOption(trip, optionValue) {
  const vehicle = trip.vehicle ?? {}

  if (Array.isArray(vehicle.options) && vehicle.options.includes(optionValue)) {
    return true
  }

  switch (optionValue) {
    case 'wifi':
      return Boolean(vehicle.hasWifi ?? vehicle.wifi ?? trip.hasWifi ?? trip.wifi)

    case 'airConditioning':
      return Boolean(
        vehicle.hasAirConditioning ??
        vehicle.airConditioning ??
        trip.hasAirConditioning ??
        trip.airConditioning
      )

    case 'toilet':
      return Boolean(vehicle.hasToilet ?? vehicle.toilet ?? trip.hasToilet ?? trip.toilet)

    case 'tv':
      return Boolean(vehicle.hasTv ?? vehicle.tv ?? trip.hasTv ?? trip.tv)

    case 'refreshments':
      return Boolean(
        vehicle.hasRefreshments ??
        vehicle.refreshments ??
        vehicle.catering ??
        trip.hasRefreshments ??
        trip.refreshments ??
        trip.catering
      )

    default:
      return false
  }
}

const availableOptions = computed(() =>
  VEHICLE_OPTIONS
    .filter(option => props.trips.some(trip => hasVehicleOption(trip, option.value)))
    .map(option => ({
      value: option.value,
      label: `${option.icon} ${option.label}`,
    }))
)

const selectedOptionsLabels = computed(() =>
  availableOptions.value
    .filter(option => selectedOptions.value.includes(option.value))
    .map(option => option.label)
)

const priceBounds = computed(() => {
  const prices = props.trips
    .map(t => Number(t.price))
    .filter(p => !Number.isNaN(p))

  if (!prices.length) return { min: 0, max: 100 }

  return {
    min: Math.floor(Math.min(...prices)),
    max: Math.ceil(Math.max(...prices)),
  }
})

const localMinPrice = ref(0)
const localMaxPrice = ref(100)

watch(priceBounds, ({ min, max }) => {
  localMinPrice.value = min
  localMaxPrice.value = max
}, { immediate: true })

watch(localMinPrice, v => {
  if (v > localMaxPrice.value) localMinPrice.value = localMaxPrice.value
})

watch(localMaxPrice, v => {
  if (v < localMinPrice.value) localMaxPrice.value = localMinPrice.value
})

const priceProgressStyle = computed(() => {
  const { min, max } = priceBounds.value
  const range = Math.max(max - min, 1)
  const left = ((localMinPrice.value - min) / range) * 100
  const right = ((localMaxPrice.value - min) / range) * 100

  return {
    left: `${left}%`,
    width: `${Math.max(right - left, 0)}%`,
  }
})

const filteredTrips = computed(() =>
  props.trips.filter(trip => {
    const price = Number(trip.price)
    const raw = trip.company ?? trip.transportCompany ?? trip.operator ?? null
    const companyValue = typeof raw === 'object'
      ? (raw?.id ?? raw?.slug ?? raw?.name)
      : raw

    return (
      !Number.isNaN(price) &&
      price >= localMinPrice.value &&
      price <= localMaxPrice.value &&
      (!selectedCompanies.value.length || selectedCompanies.value.includes(companyValue)) &&
      (!selectedOptions.value.length || selectedOptions.value.every(optionValue => hasVehicleOption(trip, optionValue)))
    )
  })
)

watch(filteredTrips, val => emit('update:filtered', val), { immediate: true })

function toggleCompany(v) {
  selectedCompanies.value = selectedCompanies.value.includes(v)
    ? selectedCompanies.value.filter(x => x !== v)
    : [...selectedCompanies.value, v]
}

function toggleOption(value) {
  selectedOptions.value = selectedOptions.value.includes(value)
    ? selectedOptions.value.filter(x => x !== value)
    : [...selectedOptions.value, value]
}

function resetFilters() {
  localMinPrice.value = priceBounds.value.min
  localMaxPrice.value = priceBounds.value.max
  selectedCompanies.value = []
  selectedOptions.value = []
  companiesOpen.value = false
  optionsOpen.value = false
}
</script>

<style scoped>
.trip-filters {
  gap: 1rem;
  padding: 1rem !important;
}

.trip-filters__grid {
  display: grid;
  grid-template-columns: minmax(200px, 1.2fr) minmax(280px, 2fr);
  gap: 1rem;
  width: 100%;
  align-items: stretch;
}

/* Colonne droite : flex column avec footer poussé en bas */
.trip-filters__right {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
}

.trip-filters__dropdowns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.trip-filters__footer {
  width: 100%;
  justify-content: flex-end;
  align-items: center;
}


/* Jauge prix */
.price-filter__values {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: var(--color-jungle);
  font-size: 0.95rem;
}

.price-filter__slider-wrap {
  position: relative;
  height: 26px;
  display: flex;
  align-items: center;
}

.price-filter__track,
.price-filter__range {
  position: absolute;
  left: 0;
  right: 0;
  height: 8px;
  border-radius: 999px;
}

.price-filter__track {
  background: var(--color-grey-medium);
}

.price-filter__range {
  right: auto;
  background: linear-gradient(90deg, var(--color-espoir) 0%, var(--color-feuille) 60%, var(--color-jungle) 100%);
  box-shadow: var(--shadow-xs);
}

.price-filter__input {
  position: absolute;
  left: 0;
  width: 100%;
  margin: 0;
  height: 26px;
  appearance: none;
  -webkit-appearance: none;
  background: transparent !important;
  pointer-events: none;
  border: none;
  padding: 0;
}

.price-filter__input--min {
  z-index: 3;
}

.price-filter__input--max {
  z-index: 4;
}

.price-filter__input::-webkit-slider-runnable-track {
  height: 8px;
  background: transparent;
}

.price-filter__input::-moz-range-track {
  height: 8px;
  background: transparent;
}

.price-filter__input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-feuille);
  border: 2px solid var(--bg-primary);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  margin-top: -5px;
}

.price-filter__input::-moz-range-thumb {
  pointer-events: auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--color-feuille);
  border: 2px solid var(--bg-primary);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
}

/* Compact select */
.compact-select {
  position: relative;
}

.compact-select__trigger-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.compact-select__menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  max-height: 240px;
  overflow-y: auto;
  padding: 0.4rem;
  background: var(--bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  box-shadow: var(--shadow-md);
}

.compact-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  border: 1px solid transparent;
  border-radius: var(--border-radius-sm);
  background: transparent;
  color: var(--color-text-primary);
  padding: 0.55rem 0.75rem;
  text-align: left;
  font-family: var(--font-family-text);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}

.compact-select__option:hover {
  background: var(--color-grey-light);
  color: var(--color-feuille);
}

.compact-select__option.is-selected {
  background: var(--color-pastel-green);
  color: var(--color-jungle);
  border-color: var(--color-feuille);
}

.compact-select__check {
  color: var(--color-feuille);
  font-weight: 700;
  flex-shrink: 0;
}

.compact-select__empty {
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-style: italic;
}

@media (max-width: 980px) {

  .trip-filters__grid,
  .trip-filters__dropdowns {
    grid-template-columns: 1fr;
  }
}
</style>
